import {
  addFaqsNotesDocuments,
  deleteFaqsNotesDocuments,
  fetchServiceRequestFaqsNotesDocuments,
  updateFaqsNotesDocuments,
} from 'src/services/client';
import { convertType } from 'src/components/File/filetypehandler';
import {
  DocumentApiData,
  DocumentFormData,
  prepareDocumentForApiUploadToS3,
} from 'src/utils/S3UploadDocumentsUtility';
import { toast } from 'react-toastify';
import { transformServiceRequestFaqsNotesDocuments } from 'src/pages/ServiceRequest/CustomHooks/helpers';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import {
  prepareCreateNoteDataForAPI,
  prepareUpdateNoteDataForAPI,
} from 'src/SharedCustomHooks/UseNotesAPI/helpers';
import {
  INoteFormConfig,
  IRawNoteFormData,
} from 'src/SharedCustomHooks/UseNotesAPI/types';

interface UseDocumentSaveOptions {
  onSuccess?: () => void;
  additionalData?: Record<string, any>;
  customErrorMessage?: string;
}

interface UseNotesAPIState {
  notes: any[];
  documents: any[];
  faqsNotesDocuments: any[];
  loadingNotesDocs: boolean;
  error: string | null;
}

export const useNotesDocsAPI = () => {
  const user: any = useSelector((state: any) => selectAuthenticatedUser(state));

  const [state, setState] = useState<UseNotesAPIState>({
    notes: [],
    documents: [],
    faqsNotesDocuments: [],
    loadingNotesDocs: false,
    error: null,
  });

  const setLoadingNotesDocs = (loadingNotes: boolean) => {
    setState((prev: UseNotesAPIState) => ({
      ...prev,
      loadingNotesDocs: loadingNotes,
    }));
  };

  const setError = (error: string | null) => {
    setState((prev: UseNotesAPIState) => ({ ...prev, error }));
  };

  const fetchNotesAndDocuments = async (referenceId: string): Promise<void> => {
    try {
      setLoadingNotesDocs(true);

      const response: any =
        await fetchServiceRequestFaqsNotesDocuments(referenceId);

      if (response.ok || response.status === 200) {
        const rawData = await response.json();

        const transformedData: any = transformServiceRequestFaqsNotesDocuments(
          rawData,
          user
        );

        setState((prev: UseNotesAPIState) => ({
          ...prev,
          notes: transformedData.notes || [],
          documents: transformedData.documents || [],
          faqsNotesDocuments: transformedData.faqsNotesDocuments || [],
        }));
      } else {
        console.warn('Failed to fetch notes and documents');
      }
    } catch (error: any) {
      console.error('Error fetching notes and documents:', error);
      setError(error.message || 'Failed to fetch notes and documents');
    } finally {
      setLoadingNotesDocs(false);
    }
  };

  const sendDocumentToAPI = async (data: any, referenceId: string) => {
    try {
      setLoadingNotesDocs(true);
      setError(null);

      const response: any = await addFaqsNotesDocuments(data);

      if (response.ok || response.status === 200) {
        const responseData = await response.json();

        toast.success(`ⓘ Document has been added.`, {
          autoClose: 10000,
          position: 'top-center',
        });

        setState((prev: UseNotesAPIState) => ({
          ...prev,
          faqsNotesDocuments: [...prev.faqsNotesDocuments, responseData.data],
        }));

        await fetchNotesAndDocuments(referenceId);
      } else {
        throw new Error(`${convertType(data.Type)} adding failed`);
      }
    } catch (error: any) {
      console.error('Error saving faqs/notes/documents:', error);

      const convertedType = convertType(data.Type);
      const errorMessage =
        error.message || `${convertedType} adding failed, please try again.`;

      setError(errorMessage);

      toast.error(`🚫 ${errorMessage}`, {
        autoClose: 12000,
        position: 'top-center',
      });
    } finally {
      setLoadingNotesDocs(false);
    }
  };

  const saveNotes = async (
    rawNoteData: IRawNoteFormData,
    referenceId: string,
    noteConfig: INoteFormConfig
  ): Promise<void> => {
    const finalAPINoteData: any = prepareCreateNoteDataForAPI(
      rawNoteData,
      referenceId,
      user,
      noteConfig
    );

    try {
      setLoadingNotesDocs(true);
      setError(null);

      const response: any = await addFaqsNotesDocuments(finalAPINoteData);

      if (response.ok || response.status === 200) {
        const responseData = await response.json();

        toast.success(`ⓘ Note has been added.`, {
          autoClose: 10000,
          position: 'top-center',
        });

        setState((prev: UseNotesAPIState) => ({
          ...prev,
          faqsNotesDocuments: [...prev.faqsNotesDocuments, responseData.data],
        }));

        await fetchNotesAndDocuments(referenceId);
      } else {
        throw new Error(`Note adding failed`);
      }
    } catch (error: any) {
      console.error('Error saving note:', error);

      const errorMessage =
        error.message || `Note adding failed, please try again.`;

      setError(errorMessage);

      toast.error(`🚫 ${errorMessage}`, {
        autoClose: 12000,
        position: 'top-center',
      });
    } finally {
      setLoadingNotesDocs(false);
    }
  };

  const saveDocument = async (
    document: DocumentFormData,
    referenceId: string,
    userId: string,
    dynamicAdditionalData?: Record<string, any>,
    options: UseDocumentSaveOptions = {}
  ): Promise<void> => {
    try {
      const { additionalData } = options;

      const apiData: DocumentApiData = await prepareDocumentForApiUploadToS3(
        document,
        referenceId,
        userId,
        { ...additionalData, ...dynamicAdditionalData }
      );

      if (!apiData) {
        toast.error(
          'Failed to prepare this document for upload. Please try again or contact Juniper support if the issue persists.',
          { autoClose: 12000, position: 'top-center' }
        );
        return;
      }

      await sendDocumentToAPI(apiData, referenceId);
    } catch (error) {
      toast.error(
        'Document upload failed. Please check your connection and try again or contact Juniper support if the issue persists.',
        { autoClose: 12000, position: 'top-center' }
      );
      console.error('Document upload error:', error);
    }
  };

  const deleteNote = async (
    noteData: any,
    referenceId: string
  ): Promise<void> => {
    try {
      setLoadingNotesDocs(true);
      setError(null);

      const response: any = await deleteFaqsNotesDocuments(noteData.id);

      if (response.ok || response.status === 200) {
        await response.json();

        toast.success(`ⓘ Note deleted successfully.`, {
          autoClose: 10000,
          position: 'top-center',
        });

        setState((prev: UseNotesAPIState) => ({
          ...prev,
          faqsNotesDocuments: prev.faqsNotesDocuments.filter(
            (item: any) => item.id !== noteData.id
          ),
          notes: prev.notes.filter((item: any) => item.id !== noteData.id),
        }));

        await fetchNotesAndDocuments(referenceId);
      } else {
        throw new Error(`Note delete failed, please try again.`);
      }
    } catch (error: any) {
      console.error('Error deleting note:', error);

      const errorMessage =
        error.message || 'Note deletion failed, please try again.';

      setError(errorMessage);

      toast.error(`🚫 ${errorMessage}`, {
        autoClose: 12000,
        position: 'top-center',
      });
    } finally {
      setLoadingNotesDocs(false);
    }
  };

  const deleteDocument = async (
    documentData: any,
    referenceId: string
  ): Promise<void> => {
    try {
      setLoadingNotesDocs(true);
      setError(null);

      const response: any = await deleteFaqsNotesDocuments(documentData.id);

      if (response.ok || response.status === 200) {
        await response.json();

        toast.success(`ⓘ Document deleted successfully.`, {
          autoClose: 10000,
          position: 'top-center',
        });

        setState((prev: UseNotesAPIState) => ({
          ...prev,
          faqsNotesDocuments: prev.faqsNotesDocuments.filter(
            (item: any) => item.id !== documentData.id
          ),
          documents: prev.documents.filter(
            (item: any) => item.id !== documentData.id
          ),
        }));

        await fetchNotesAndDocuments(referenceId);
      } else {
        throw new Error(`Document delete failed, please try again.`);
      }
    } catch (error: any) {
      console.error('Error deleting document:', error);

      const errorMessage =
        error.message || 'Document deletion failed, please try again.';

      setError(errorMessage);

      toast.error(`🚫 ${errorMessage}`, {
        autoClose: 12000,
        position: 'top-center',
      });
    } finally {
      setLoadingNotesDocs(false);
    }
  };

  const updateNote = async (
    rawNoteData: IRawNoteFormData,
    referenceId: string,
    noteConfig: INoteFormConfig,
    ticketNumber?: string
  ): Promise<void> => {
    const finalAPINoteData: any = prepareUpdateNoteDataForAPI(
      rawNoteData,
      referenceId,
      user,
      noteConfig,
      ticketNumber
    );

    try {
      setLoadingNotesDocs(true);
      setError(null);

      const response: any = await updateFaqsNotesDocuments(finalAPINoteData);

      if (response.ok || response.status === 200) {
        const responseData = await response.json();

        toast.success(`ⓘ Note has been updated.`, {
          autoClose: 10000,
          position: 'top-center',
        });

        setState((prev: UseNotesAPIState) => ({
          ...prev,
          faqsNotesDocuments: prev.faqsNotesDocuments.map((item: any) =>
            item.id === finalAPINoteData.id ? responseData.data : item
          ),
          notes: prev.notes.map((item: any) =>
            item.id === finalAPINoteData.id ? responseData.data : item
          ),
        }));

        await fetchNotesAndDocuments(referenceId);
      } else {
        throw new Error(`Note updating failed`);
      }
    } catch (error: any) {
      console.error('Error updating note:', error);

      const errorMessage =
        error.message || `Note updating failed, please try again.`;

      setError(errorMessage);

      toast.error(`🚫 ${errorMessage}`, {
        autoClose: 12000,
        position: 'top-center',
      });
    } finally {
      setLoadingNotesDocs(false);
    }
  };

  const updateDocument = async (
    document: DocumentFormData,
    referenceId: string,
    userId: string,
    documentId: string,
    dynamicAdditionalData?: Record<string, any>,
    options: UseDocumentSaveOptions = {}
  ): Promise<void> => {
    try {
      setLoadingNotesDocs(true);
      setError(null);

      const { additionalData } = options;

      const apiData: DocumentApiData = await prepareDocumentForApiUploadToS3(
        document,
        referenceId,
        userId,
        { ...additionalData, ...dynamicAdditionalData, id: documentId }
      );

      if (!apiData) {
        toast.error(
          'Failed to prepare this document for update. Please try again or contact Juniper support if the issue persists.',
          { autoClose: 12000, position: 'top-center' }
        );
        return;
      }

      const response: any = await updateFaqsNotesDocuments(apiData);

      if (response.ok || response.status === 200) {
        const responseData = await response.json();

        toast.success(`ⓘ Document has been updated.`, {
          autoClose: 10000,
          position: 'top-center',
        });

        setState((prev: UseNotesAPIState) => ({
          ...prev,
          faqsNotesDocuments: prev.faqsNotesDocuments.map((item: any) =>
            item.id === documentId ? responseData.data : item
          ),
          documents: prev.documents.map((item: any) =>
            item.id === documentId ? responseData.data : item
          ),
        }));

        await fetchNotesAndDocuments(referenceId);
      } else {
        throw new Error(`Document updating failed`);
      }
    } catch (error: any) {
      console.error('Error updating document:', error);

      const errorMessage =
        error.message || 'Document update failed, please try again.';

      setError(errorMessage);

      toast.error(`🚫 ${errorMessage}`, {
        autoClose: 12000,
        position: 'top-center',
      });
    } finally {
      setLoadingNotesDocs(false);
    }
  };

  const clearNotesState = () => {
    setState((prev: UseNotesAPIState) => ({
      ...prev,
      notes: [],
      documents: [],
      faqsNotesDocuments: [],
    }));
  };

  return {
    notes: state.notes,
    documents: state.documents,
    faqsNotesDocuments: state.faqsNotesDocuments,
    loadingNotesDocs: state.loadingNotesDocs,
    notesError: state.error,
    fetchNotesAndDocuments,
    // Save methods.
    saveNotes,
    saveDocument,
    // Delete methods.
    deleteNote,
    deleteDocument,
    // Update methods.
    updateNote,
    updateDocument,
    clearNotesState,
  };
};
